import React, { useState, useMemo } from 'react';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer,
} from 'recharts';
import {
  LayoutDashboard, FolderTree, Users, Mail, Wallet, BarChart3, ScrollText, Settings,
  Globe, ChevronDown, ChevronRight, Lock, Eye, EyeOff, Send, X, Bell, Search,
  ArrowUpRight, ArrowDownRight, CircleDollarSign, ReceiptText, CreditCard, QrCode,
  Smartphone, PenLine, CheckCircle2, UserPlus, ShieldCheck, Crown, Building2, CalendarRange,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../../components/ui/sheet';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Toaster, toast } from 'sonner';

/* ============================================================
   PayMig — Owner ERP Dashboard PROTOTYPE (Slice C preview)
   Static, clickable, mock data only. No backend calls.
   Design system: /app/design_guidelines.json
   ============================================================ */

const TEAL = ['#0f766e', '#14b8a6', '#2dd4bf', '#5eead4', '#99f6e4', '#134e4a'];

const T = {
  en: {
    prototype: 'PROTOTYPE — mock data',
    nav_dashboard: 'Dashboard', nav_divisions: 'Divisions', nav_members: 'Members',
    nav_invitations: 'Invitations', nav_swish: 'Swish Accounts', nav_reports: 'Reports',
    nav_audit: 'Audit Log', nav_settings: 'Settings',
    owner: 'Owner', search: 'Search…',
    range_7d: 'Last 7 days', range_30d: 'Last 30 days', range_90d: 'Last 90 days', range_ytd: 'Year to date',
    kpi_collected: 'Total collected', kpi_tx: 'Transactions', kpi_avg: 'Avg. payment', kpi_payers: 'Unique payers',
    vs_prev: 'vs previous period',
    collections_over_time: 'Collections over time',
    recent_activity: 'Recent activity',
    by_division: 'Collections by division',
    payment_mix: 'Payment mix',
    mix_app2app: 'Swish App2App', mix_qr: 'Guest QR', mix_manual: 'Manual',
    divisions_title: 'Divisions', divisions_sub: 'ERP view — money stays in each division pool',
    th_division: 'Division', th_admin: 'Division admin', th_mods: 'Moderators',
    th_payout: 'Payout Swish', th_pool: 'Pool', th_collected: 'Collected', th_status: 'Status',
    status_active: 'Active', status_paused: 'Paused', status_setup: 'Setup',
    seats_plan: 'Seats & Plan', seats_used: 'seats used', divisions_used: 'divisions used',
    manage_plan: 'Manage plan', plan_name: 'Organisation Pro',
    pending_inv: 'Pending invitations', resend: 'Resend', revoke: 'Revoke',
    expires: 'Expires', role_admin: 'Division admin', role_mod: 'Moderator',
    swish_accounts: 'Swish accounts', primary: 'Primary', linked: 'linked divisions',
    add_account: 'Add account',
    drawer_desc: 'Division drill-down (read-only prototype)',
    d_admin: 'Admin', d_mods: 'Moderators', d_payout: 'Payout Swish',
    d_collected: 'Collected (period)', d_pool: 'Current pool', d_tx: 'Transactions',
    d_recent_tx: 'Recent transactions', view_report: 'View report', manage_members: 'Manage members',
    proto_toast: 'Prototype: this section ships in a later slice',
    resent_toast: 'Invitation re-sent to', revoked_toast: 'Invitation revoked for',
    revealed: 'Payout number revealed (owner-only view)',
    no_inv: 'No pending invitations',
    guest: 'Guest', all_divisions: 'All divisions',
  },
  sv: {
    prototype: 'PROTOTYP — testdata',
    nav_dashboard: 'Översikt', nav_divisions: 'Divisioner', nav_members: 'Medlemmar',
    nav_invitations: 'Inbjudningar', nav_swish: 'Swish-konton', nav_reports: 'Rapporter',
    nav_audit: 'Granskningslogg', nav_settings: 'Inställningar',
    owner: 'Ägare', search: 'Sök…',
    range_7d: 'Senaste 7 dagarna', range_30d: 'Senaste 30 dagarna', range_90d: 'Senaste 90 dagarna', range_ytd: 'Hittills i år',
    kpi_collected: 'Totalt insamlat', kpi_tx: 'Transaktioner', kpi_avg: 'Snittbetalning', kpi_payers: 'Unika betalare',
    vs_prev: 'jämfört med föregående period',
    collections_over_time: 'Insamlingar över tid',
    recent_activity: 'Senaste aktivitet',
    by_division: 'Insamlat per division',
    payment_mix: 'Betalningsmix',
    mix_app2app: 'Swish App2App', mix_qr: 'Gäst-QR', mix_manual: 'Manuell',
    divisions_title: 'Divisioner', divisions_sub: 'ERP-vy — pengarna stannar i varje divisions pott',
    th_division: 'Division', th_admin: 'Divisionsadmin', th_mods: 'Moderatorer',
    th_payout: 'Utbetalnings-Swish', th_pool: 'Pott', th_collected: 'Insamlat', th_status: 'Status',
    status_active: 'Aktiv', status_paused: 'Pausad', status_setup: 'Uppsättning',
    seats_plan: 'Platser & Plan', seats_used: 'platser använda', divisions_used: 'divisioner använda',
    manage_plan: 'Hantera plan', plan_name: 'Organisation Pro',
    pending_inv: 'Väntande inbjudningar', resend: 'Skicka igen', revoke: 'Återkalla',
    expires: 'Går ut', role_admin: 'Divisionsadmin', role_mod: 'Moderator',
    swish_accounts: 'Swish-konton', primary: 'Primärt', linked: 'kopplade divisioner',
    add_account: 'Lägg till konto',
    drawer_desc: 'Divisionsdetaljer (skrivskyddad prototyp)',
    d_admin: 'Admin', d_mods: 'Moderatorer', d_payout: 'Utbetalnings-Swish',
    d_collected: 'Insamlat (period)', d_pool: 'Aktuell pott', d_tx: 'Transaktioner',
    d_recent_tx: 'Senaste transaktioner', view_report: 'Visa rapport', manage_members: 'Hantera medlemmar',
    proto_toast: 'Prototyp: denna sektion kommer i en senare etapp',
    resent_toast: 'Inbjudan skickad igen till', revoked_toast: 'Inbjudan återkallad för',
    revealed: 'Utbetalningsnummer visat (endast ägare)',
    no_inv: 'Inga väntande inbjudningar',
    guest: 'Gäst', all_divisions: 'Alla divisioner',
  },
};

/* ---------- Mock data ---------- */

const ORGS = [
  { id: 'org1', name: 'Sollentuna FK', initials: 'SF' },
  { id: 'org2', name: 'St Bonifatius Kyrka', initials: 'SB' },
];

const RANGE_SCALE = { '7d': 0.18, '30d': 1, '90d': 2.7, 'ytd': 5.4 };

const HERO_DATA = {
  '7d': [
    { label: 'Mon', v: 3200 }, { label: 'Tue', v: 4100 }, { label: 'Wed', v: 2800 },
    { label: 'Thu', v: 5600 }, { label: 'Fri', v: 7400 }, { label: 'Sat', v: 9800 }, { label: 'Sun', v: 6100 },
  ],
  '30d': [
    { label: 'W1', v: 18400 }, { label: 'W2', v: 22100 }, { label: 'W3', v: 19600 },
    { label: 'W4', v: 27300 }, { label: 'W5', v: 24800 },
  ],
  '90d': [
    { label: 'Mar', v: 61200 }, { label: 'Apr', v: 74800 }, { label: 'May', v: 88400 },
  ],
  'ytd': [
    { label: 'Jan', v: 41200 }, { label: 'Feb', v: 52400 }, { label: 'Mar', v: 61200 },
    { label: 'Apr', v: 74800 }, { label: 'May', v: 88400 }, { label: 'Jun', v: 96100 },
  ],
};

const SPARK = {
  a: [4, 6, 5, 8, 7, 10, 12], b: [10, 9, 11, 12, 11, 13, 14],
  c: [6, 5, 7, 6, 8, 7, 9], d: [3, 4, 4, 6, 5, 7, 8],
};

const DIVISIONS = [
  {
    id: 'd1', name: 'P12 Ungdomslag', admin: 'Anna Lindqvist', adminEmail: 'anna.lindqvist@sollentunafk.se',
    mods: ['Johan Berg', 'Sara Ek'], payout: '123-456 78 90', pool: 12400, collected: 48200, tx: 214, status: 'active',
    trend: [3, 5, 4, 7, 6, 9, 11],
  },
  {
    id: 'd2', name: 'F14 Flickor', admin: 'Maria Söderström', adminEmail: 'maria.s@sollentunafk.se',
    mods: ['Erik Nyström'], payout: '123-311 22 44', pool: 8150, collected: 31600, tx: 142, status: 'active',
    trend: [2, 4, 3, 5, 6, 6, 8],
  },
  {
    id: 'd3', name: 'Herrlag A', admin: 'Peter Åkesson', adminEmail: 'peter.akesson@sollentunafk.se',
    mods: ['Lars Holm', 'Nina Falk', 'Oskar Lund'], payout: '123-198 76 54', pool: 21900, collected: 67450, tx: 305, status: 'active',
    trend: [6, 7, 9, 8, 11, 12, 14],
  },
  {
    id: 'd4', name: 'Damlag', admin: 'Karin Öberg', adminEmail: 'karin.oberg@sollentunafk.se',
    mods: ['Eva Ström'], payout: '123-654 32 10', pool: 9800, collected: 28900, tx: 118, status: 'active',
    trend: [3, 3, 5, 4, 6, 7, 7],
  },
  {
    id: 'd5', name: 'Kiosk & Café', admin: 'Håkan Vikström', adminEmail: 'hakan.v@sollentunafk.se',
    mods: [], payout: '123-777 88 99', pool: 4300, collected: 15750, tx: 421, status: 'active',
    trend: [8, 9, 7, 10, 9, 11, 12],
  },
  {
    id: 'd6', name: 'Events & Cuper', admin: 'Lena Marklund', adminEmail: 'lena.marklund@sollentunafk.se',
    mods: ['Tomas Rehn'], payout: '123-505 05 05', pool: 0, collected: 6200, tx: 34, status: 'paused',
    trend: [4, 2, 3, 1, 2, 1, 1],
  },
];

const MIX = [
  { key: 'mix_app2app', value: 58, icon: Smartphone },
  { key: 'mix_qr', value: 31, icon: QrCode },
  { key: 'mix_manual', value: 11, icon: PenLine },
];

const ACTIVITY = [
  { icon: CircleDollarSign, tone: 'teal', en: '250 kr received — Herrlag A (Swish App2App)', sv: '250 kr mottaget — Herrlag A (Swish App2App)', time: '2 min' },
  { icon: QrCode, tone: 'teal', en: '120 kr guest payment — Kiosk & Café', sv: '120 kr gästbetalning — Kiosk & Café', time: '18 min' },
  { icon: UserPlus, tone: 'blue', en: 'Sara Ek accepted moderator invite — P12', sv: 'Sara Ek accepterade moderatorinbjudan — P12', time: '1 h' },
  { icon: ShieldCheck, tone: 'amber', en: 'Payout Swish changed — Damlag (audit logged)', sv: 'Utbetalnings-Swish ändrad — Damlag (loggad)', time: '3 h' },
  { icon: CircleDollarSign, tone: 'teal', en: '500 kr received — F14 Flickor (fixed amount)', sv: '500 kr mottaget — F14 Flickor (fast belopp)', time: '5 h' },
  { icon: ReceiptText, tone: 'slate', en: 'Manual entry: 1 340 kr — Kiosk & Café', sv: 'Manuell post: 1 340 kr — Kiosk & Café', time: '8 h' },
  { icon: CheckCircle2, tone: 'teal', en: 'Monthly report generated — Herrlag A', sv: 'Månadsrapport skapad — Herrlag A', time: '1 d' },
];

const INITIAL_INVITES = [
  { id: 'i1', name: 'Jonas Wallin', email: 'jonas.wallin@gmail.com', role: 'role_admin', division: 'Events & Cuper', expires: '2026-07-18' },
  { id: 'i2', name: 'Emma Dahl', email: 'emma.dahl@hotmail.se', role: 'role_mod', division: 'Herrlag A', expires: '2026-07-16' },
  { id: 'i3', name: 'Ali Hassan', email: 'ali.hassan@outlook.com', role: 'role_mod', division: 'Kiosk & Café', expires: '2026-07-20' },
];

const SWISH_ACCOUNTS = [
  { id: 's1', label: 'Sollentuna FK Huvudkonto', number: '123-000 11 22', primary: true, linked: 3 },
  { id: 's2', label: 'SFK Ungdom', number: '123-456 78 90', primary: false, linked: 2 },
  { id: 's3', label: 'SFK Kiosk', number: '123-777 88 99', primary: false, linked: 1 },
];

const DIV_TX = [
  { en: 'Match fee — Guest QR', sv: 'Matchavgift — Gäst-QR', amount: 150, time: '10:42' },
  { en: 'Training fee — App2App', sv: 'Träningsavgift — App2App', amount: 250, time: '09:15' },
  { en: 'Cup registration — App2App', sv: 'Cupanmälan — App2App', amount: 500, time: 'Yesterday' },
  { en: 'Kiosk sales — Manual', sv: 'Kioskförsäljning — Manuell', amount: 1340, time: 'Yesterday' },
];

/* ---------- helpers ---------- */

const fmtKr = (n) => new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 0 }).format(Math.round(n)) + ' kr';
const maskSwish = (s) => '•••-••• •• ' + s.slice(-2);

const toneBg = {
  teal: 'bg-teal-50 text-teal-700', blue: 'bg-sky-50 text-sky-700',
  amber: 'bg-amber-50 text-amber-700', slate: 'bg-slate-100 text-slate-600',
};

function Spark({ data, color = '#0d9488' }) {
  const d = data.map((v, i) => ({ i, v }));
  return (
    <ResponsiveContainer width="100%" height={44}>
      <AreaChart data={d} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
        <defs>
          <linearGradient id={`sg-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.28} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <Area type="monotone" dataKey="v" stroke={color} strokeWidth={2} fill={`url(#sg-${color.replace('#', '')})`} isAnimationActive={false} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function KpiCard({ title, value, delta, up, spark, testId }) {
  return (
    <div data-testid={testId} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex flex-col gap-1 min-w-0">
      <div className="text-[12px] font-medium text-slate-500">{title}</div>
      <div className="flex items-end justify-between gap-2">
        <div className="text-[26px] leading-none font-semibold text-slate-900 tabular-nums" style={{ fontFamily: "'Work Sans', sans-serif" }}>{value}</div>
        <span className={`inline-flex items-center gap-0.5 text-[12px] font-semibold px-1.5 py-0.5 rounded-md ${up ? 'text-emerald-700 bg-emerald-50' : 'text-rose-700 bg-rose-50'}`}>
          {up ? <ArrowUpRight size={13} /> : <ArrowDownRight size={13} />}{delta}
        </span>
      </div>
      <div className="-mx-1 mt-1"><Spark data={spark} /></div>
    </div>
  );
}

function StatusBadge({ status, t }) {
  if (status === 'active') return <Badge className="bg-teal-50 text-teal-700 border border-teal-200 hover:bg-teal-50 shadow-none font-medium">{t.status_active}</Badge>;
  if (status === 'paused') return <Badge className="bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-50 shadow-none font-medium">{t.status_paused}</Badge>;
  return <Badge className="bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-100 shadow-none font-medium">{t.status_setup}</Badge>;
}

function PayoutCell({ number, revealed, onToggle, t }) {
  return (
    <div className="flex items-center gap-1.5 tabular-nums">
      <Lock size={13} className="text-slate-400 shrink-0" />
      <span className="text-[13px]">{revealed ? number : maskSwish(number)}</span>
      <button
        type="button"
        className="text-slate-400 hover:text-teal-700 transition-colors"
        onClick={(e) => { e.stopPropagation(); onToggle(); if (!revealed) toast.info(t.revealed); }}
        data-testid="payout-reveal-toggle"
      >
        {revealed ? <EyeOff size={14} /> : <Eye size={14} />}
      </button>
    </div>
  );
}

/* ---------- main ---------- */

export default function OwnerDashboardPrototype() {
  const [lang, setLang] = useState('en');
  const [org, setOrg] = useState(ORGS[0]);
  const [range, setRange] = useState('30d');
  const [nav, setNav] = useState('dashboard');
  const [revealed, setRevealed] = useState({});
  const [invites, setInvites] = useState(INITIAL_INVITES);
  const [sel, setSel] = useState(null); // selected division for drawer

  const t = T[lang];
  const scale = RANGE_SCALE[range];

  const kpis = useMemo(() => ({
    collected: 198100 * scale,
    tx: Math.round(1234 * scale),
    avg: 161 + (range === '7d' ? 12 : 0),
    payers: Math.round(486 * scale),
  }), [scale, range]);

  const heroData = HERO_DATA[range];
  const barData = useMemo(() => DIVISIONS.map((d) => ({ name: d.name.split(' ')[0], full: d.name, v: Math.round(d.collected * scale) })), [scale]);

  const NAV_ITEMS = [
    { id: 'dashboard', icon: LayoutDashboard, label: t.nav_dashboard },
    { id: 'divisions', icon: FolderTree, label: t.nav_divisions },
    { id: 'members', icon: Users, label: t.nav_members },
    { id: 'invitations', icon: Mail, label: t.nav_invitations, count: invites.length },
    { id: 'swish', icon: Wallet, label: t.nav_swish },
    { id: 'reports', icon: BarChart3, label: t.nav_reports },
    { id: 'audit', icon: ScrollText, label: t.nav_audit },
    { id: 'settings', icon: Settings, label: t.nav_settings },
  ];

  const rangeLabel = { '7d': t.range_7d, '30d': t.range_30d, '90d': t.range_90d, 'ytd': t.range_ytd };

  const handleNav = (id) => {
    setNav(id);
    if (id !== 'dashboard') toast(t.proto_toast, { icon: '🧪' });
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Work+Sans:wght@400;500;600;700&family=IBM+Plex+Sans:wght@400;500;600&display=swap');`}</style>
      <Toaster position="top-right" richColors />

      {/* ===== Sidebar ===== */}
      <aside className="fixed inset-y-0 left-0 w-[232px] bg-white border-r border-slate-200 flex flex-col z-30">
        <div className="h-14 px-4 flex items-center gap-2.5 border-b border-slate-100">
          <div className="w-8 h-8 rounded-lg bg-teal-600 flex items-center justify-center text-white font-bold text-sm" style={{ fontFamily: "'Work Sans', sans-serif" }}>P</div>
          <div className="leading-tight">
            <div className="font-semibold text-[14px]" style={{ fontFamily: "'Work Sans', sans-serif" }}>PayMig</div>
            <div className="text-[10.5px] text-slate-400 font-medium tracking-wide uppercase">Organisation</div>
          </div>
        </div>
        <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
          {NAV_ITEMS.map(({ id, icon: Icon, label, count }) => (
            <button
              key={id}
              data-testid={`nav-${id}`}
              onClick={() => handleNav(id)}
              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13.5px] font-medium transition-colors relative ${
                nav === id ? 'bg-teal-50 text-teal-700' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
              }`}
            >
              {nav === id && <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-full bg-teal-600" />}
              <Icon size={17} className={nav === id ? 'text-teal-600' : 'text-slate-400'} />
              <span className="flex-1 text-left">{label}</span>
              {count ? <span className="text-[11px] font-semibold bg-teal-600 text-white rounded-full px-1.5 py-0.5 leading-none">{count}</span> : null}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-100">
          <div className="flex items-center gap-2.5 px-2 py-1.5">
            <div className="w-8 h-8 rounded-full bg-teal-100 text-teal-800 flex items-center justify-center text-[12px] font-bold">SB</div>
            <div className="leading-tight min-w-0">
              <div className="text-[13px] font-semibold truncate">Stefan Bergström</div>
              <div className="text-[11px] text-slate-400 flex items-center gap-1"><Crown size={11} className="text-amber-500" />{t.owner}</div>
            </div>
          </div>
        </div>
      </aside>

      {/* ===== Main ===== */}
      <div className="pl-[232px]">
        {/* Topbar */}
        <header className="sticky top-0 z-20 h-14 backdrop-blur-md bg-white/80 border-b border-slate-200 flex items-center gap-3 px-5">
          {/* Org switcher */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="org-switcher" className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg hover:bg-slate-100 transition-colors">
                <div className="w-6 h-6 rounded-md bg-teal-600 text-white text-[10px] font-bold flex items-center justify-center">{org.initials}</div>
                <span className="text-[13.5px] font-semibold" style={{ fontFamily: "'Work Sans', sans-serif" }}>{org.name}</span>
                <ChevronDown size={14} className="text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-slate-400">Organisations</DropdownMenuLabel>
              {ORGS.map((o) => (
                <DropdownMenuItem key={o.id} onClick={() => { setOrg(o); toast.success(`→ ${o.name}`); }} className="gap-2">
                  <div className="w-5 h-5 rounded bg-teal-600 text-white text-[9px] font-bold flex items-center justify-center">{o.initials}</div>
                  {o.name}
                  {o.id === org.id && <CheckCircle2 size={14} className="ml-auto text-teal-600" />}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => toast(t.proto_toast, { icon: '🧪' })} className="gap-2 text-slate-500">
                <Building2 size={14} /> + New organisation
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="hidden lg:flex items-center gap-2 ml-2 flex-1 max-w-xs">
            <div className="flex items-center gap-2 w-full bg-slate-100/70 rounded-lg px-3 py-1.5 text-slate-400">
              <Search size={14} /><span className="text-[13px]">{t.search}</span>
            </div>
          </div>

          <div className="ml-auto flex items-center gap-2">
            {/* Date range */}
            <Select value={range} onValueChange={setRange}>
              <SelectTrigger data-testid="date-range-select" className="h-9 w-[180px] text-[13px] font-medium bg-white border-slate-200 gap-1.5">
                <CalendarRange size={14} className="text-teal-600 shrink-0" />
                <SelectValue>{rangeLabel[range]}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7d">{t.range_7d}</SelectItem>
                <SelectItem value="30d">{t.range_30d}</SelectItem>
                <SelectItem value="90d">{t.range_90d}</SelectItem>
                <SelectItem value="ytd">{t.range_ytd}</SelectItem>
              </SelectContent>
            </Select>

            {/* EN/SV toggle */}
            <div data-testid="lang-toggle" className="flex items-center rounded-lg border border-slate-200 bg-white overflow-hidden">
              {['en', 'sv'].map((l) => (
                <button
                  key={l}
                  data-testid={`lang-${l}`}
                  onClick={() => setLang(l)}
                  className={`px-2.5 py-1.5 text-[12px] font-semibold uppercase transition-colors ${lang === l ? 'bg-teal-600 text-white' : 'text-slate-500 hover:bg-slate-50'}`}
                >
                  {l}
                </button>
              ))}
            </div>

            <button className="relative p-2 rounded-lg hover:bg-slate-100 text-slate-500" onClick={() => toast(t.proto_toast, { icon: '🧪' })}>
              <Bell size={17} />
              <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-teal-600" />
            </button>
            <div className="w-8 h-8 rounded-full bg-teal-100 text-teal-800 flex items-center justify-center text-[12px] font-bold">SB</div>
          </div>
        </header>

        {/* Prototype banner */}
        <div className="px-6 pt-4">
          <div className="flex items-center gap-2 text-[12px] font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5 w-fit">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />{t.prototype}
          </div>
        </div>

        <main className="p-6 space-y-5 max-w-[1440px]">
          {/* KPI row */}
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
            <KpiCard testId="kpi-collected" title={t.kpi_collected} value={fmtKr(kpis.collected)} delta="12.4%" up spark={SPARK.a} />
            <KpiCard testId="kpi-tx" title={t.kpi_tx} value={new Intl.NumberFormat('sv-SE').format(kpis.tx)} delta="8.1%" up spark={SPARK.b} />
            <KpiCard testId="kpi-avg" title={t.kpi_avg} value={fmtKr(kpis.avg)} delta="2.3%" up={false} spark={SPARK.c} />
            <KpiCard testId="kpi-payers" title={t.kpi_payers} value={new Intl.NumberFormat('sv-SE').format(kpis.payers)} delta="18.9%" up spark={SPARK.d} />
          </div>

          {/* Hero chart + activity rail */}
          <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-4">
            <div data-testid="hero-chart" className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-[15px] font-semibold tracking-tight" style={{ fontFamily: "'Work Sans', sans-serif" }}>{t.collections_over_time}</h2>
                  <p className="text-[12px] text-slate-400 mt-0.5">{rangeLabel[range]} · {t.all_divisions} · SEK</p>
                </div>
                <Badge className="bg-teal-50 text-teal-700 border border-teal-200 shadow-none hover:bg-teal-50">{fmtKr(kpis.collected)}</Badge>
              </div>
              <ResponsiveContainer width="100%" height={340}>
                <AreaChart data={heroData} margin={{ top: 8, right: 8, bottom: 0, left: 4 }}>
                  <defs>
                    <linearGradient id="heroFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#14b8a6" stopOpacity={0.32} />
                      <stop offset="100%" stopColor="#14b8a6" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} width={38} />
                  <RTooltip formatter={(v) => [fmtKr(v), lang === 'sv' ? 'Insamlat' : 'Collected']} contentStyle={{ borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 13 }} />
                  <Area type="monotone" dataKey="v" stroke="#0d9488" strokeWidth={2.5} fill="url(#heroFill)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Activity rail */}
            <div data-testid="activity-rail" className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 flex flex-col max-h-[440px]">
              <h2 className="text-[15px] font-semibold tracking-tight mb-3" style={{ fontFamily: "'Work Sans', sans-serif" }}>{t.recent_activity}</h2>
              <div className="space-y-1 overflow-y-auto -mx-1">
                {ACTIVITY.map((a, i) => {
                  const Icon = a.icon;
                  return (
                    <div key={i} className="flex items-start gap-2.5 px-1 py-2 rounded-lg hover:bg-slate-50">
                      <span className={`mt-0.5 w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${toneBg[a.tone]}`}><Icon size={14} /></span>
                      <div className="min-w-0">
                        <p className="text-[12.5px] leading-snug text-slate-700">{lang === 'sv' ? a.sv : a.en}</p>
                        <p className="text-[11px] text-slate-400 mt-0.5">{a.time}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Bar + donut */}
          <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-4">
            <div data-testid="division-bar-chart" className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
              <h2 className="text-[15px] font-semibold tracking-tight mb-4" style={{ fontFamily: "'Work Sans', sans-serif" }}>{t.by_division}</h2>
              <ResponsiveContainer width="100%" height={230}>
                <BarChart data={barData} margin={{ top: 4, right: 8, bottom: 0, left: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} width={38} />
                  <RTooltip formatter={(v, n, p) => [fmtKr(v), p?.payload?.full]} contentStyle={{ borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 13 }} cursor={{ fill: '#f0fdfa' }} />
                  <Bar dataKey="v" radius={[6, 6, 0, 0]}>
                    {barData.map((_, i) => <Cell key={i} fill={TEAL[i % TEAL.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div data-testid="payment-mix-donut" className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
              <h2 className="text-[15px] font-semibold tracking-tight mb-1" style={{ fontFamily: "'Work Sans', sans-serif" }}>{t.payment_mix}</h2>
              <div className="flex items-center">
                <ResponsiveContainer width="55%" height={210}>
                  <PieChart>
                    <Pie data={MIX} dataKey="value" innerRadius={56} outerRadius={84} paddingAngle={3} strokeWidth={0}>
                      {MIX.map((_, i) => <Cell key={i} fill={TEAL[i]} />)}
                    </Pie>
                    <RTooltip formatter={(v, n, p) => [`${v}%`, t[p?.payload?.key]]} contentStyle={{ borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 13 }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-3">
                  {MIX.map((m, i) => {
                    const Icon = m.icon;
                    return (
                      <div key={m.key} className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: TEAL[i] }} />
                        <Icon size={14} className="text-slate-400 shrink-0" />
                        <span className="text-[12.5px] text-slate-600 flex-1">{t[m.key]}</span>
                        <span className="text-[13px] font-semibold tabular-nums">{m.value}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Divisions ERP table */}
          <div data-testid="divisions-table" className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <div className="px-5 pt-5 pb-3 flex items-center justify-between">
              <div>
                <h2 className="text-[15px] font-semibold tracking-tight" style={{ fontFamily: "'Work Sans', sans-serif" }}>{t.divisions_title}</h2>
                <p className="text-[12px] text-slate-400 mt-0.5">{t.divisions_sub}</p>
              </div>
              <Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white h-8 text-[13px]" onClick={() => toast(t.proto_toast, { icon: '🧪' })}>
                + {lang === 'sv' ? 'Ny division' : 'New division'}
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-left text-[11.5px] uppercase tracking-wide text-slate-400 border-y border-slate-100 bg-slate-50/60">
                    <th className="px-5 py-2.5 font-semibold">{t.th_division}</th>
                    <th className="px-3 py-2.5 font-semibold">{t.th_admin}</th>
                    <th className="px-3 py-2.5 font-semibold">{t.th_mods}</th>
                    <th className="px-3 py-2.5 font-semibold">{t.th_payout}</th>
                    <th className="px-3 py-2.5 font-semibold text-right">{t.th_pool}</th>
                    <th className="px-3 py-2.5 font-semibold text-right">{t.th_collected}</th>
                    <th className="px-3 py-2.5 font-semibold">{t.th_status}</th>
                    <th className="px-3 py-2.5" />
                  </tr>
                </thead>
                <tbody>
                  {DIVISIONS.map((d) => (
                    <tr
                      key={d.id}
                      data-testid={`division-row-${d.id}`}
                      className="border-b border-slate-100 last:border-0 hover:bg-teal-50/40 cursor-pointer transition-colors"
                      onClick={() => setSel(d)}
                    >
                      <td className="px-5 py-3">
                        <div className="font-semibold text-slate-800">{d.name}</div>
                        <div className="text-[11.5px] text-slate-400">{d.tx} tx</div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-teal-100 text-teal-800 text-[10px] font-bold flex items-center justify-center shrink-0">
                            {d.admin.split(' ').map((x) => x[0]).join('')}
                          </div>
                          <span className="text-slate-700">{d.admin}</span>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        {d.mods.length ? (
                          <div className="flex -space-x-1.5 items-center">
                            {d.mods.slice(0, 3).map((m) => (
                              <div key={m} title={m} className="w-6 h-6 rounded-full bg-slate-100 border-2 border-white text-slate-600 text-[10px] font-bold flex items-center justify-center">
                                {m.split(' ').map((x) => x[0]).join('')}
                              </div>
                            ))}
                            <span className="pl-2.5 text-[12px] text-slate-400">{d.mods.length}</span>
                          </div>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-3 py-3">
                        <PayoutCell
                          number={d.payout}
                          revealed={!!revealed[d.id]}
                          onToggle={() => setRevealed((r) => ({ ...r, [d.id]: !r[d.id] }))}
                          t={t}
                        />
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums font-medium">{fmtKr(d.pool)}</td>
                      <td className="px-3 py-3 text-right tabular-nums font-semibold text-teal-700">{fmtKr(d.collected * scale)}</td>
                      <td className="px-3 py-3"><StatusBadge status={d.status} t={t} /></td>
                      <td className="px-3 py-3 text-slate-300"><ChevronRight size={16} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Bottom panels */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            {/* Seats & Plan */}
            <div data-testid="panel-seats-plan" className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-[15px] font-semibold tracking-tight" style={{ fontFamily: "'Work Sans', sans-serif" }}>{t.seats_plan}</h2>
                <Badge className="bg-teal-600 text-white shadow-none hover:bg-teal-600 gap-1"><Crown size={11} />{t.plan_name}</Badge>
              </div>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-[12.5px] mb-1.5">
                    <span className="text-slate-500">18 / 25 {t.seats_used}</span>
                    <span className="font-semibold text-slate-700">72%</span>
                  </div>
                  <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full bg-teal-600" style={{ width: '72%' }} /></div>
                </div>
                <div>
                  <div className="flex justify-between text-[12.5px] mb-1.5">
                    <span className="text-slate-500">6 / 10 {t.divisions_used}</span>
                    <span className="font-semibold text-slate-700">60%</span>
                  </div>
                  <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full bg-teal-400" style={{ width: '60%' }} /></div>
                </div>
                <Button variant="outline" size="sm" className="w-full h-8 text-[13px] border-slate-200" onClick={() => toast(t.proto_toast, { icon: '🧪' })}>
                  <CreditCard size={14} className="mr-1.5" />{t.manage_plan}
                </Button>
              </div>
            </div>

            {/* Pending invitations */}
            <div data-testid="panel-invitations" className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-[15px] font-semibold tracking-tight" style={{ fontFamily: "'Work Sans', sans-serif" }}>{t.pending_inv}</h2>
                <Badge className="bg-slate-100 text-slate-600 shadow-none hover:bg-slate-100">{invites.length}</Badge>
              </div>
              {invites.length === 0 ? (
                <p className="text-[13px] text-slate-400 py-6 text-center">{t.no_inv}</p>
              ) : (
                <div className="space-y-2.5">
                  {invites.map((inv) => (
                    <div key={inv.id} data-testid={`invite-${inv.id}`} className="border border-slate-100 rounded-lg px-3 py-2.5">
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-[13px] font-semibold truncate">{inv.name}</div>
                          <div className="text-[11.5px] text-slate-400 truncate">{inv.email}</div>
                        </div>
                        <Badge className={`shadow-none shrink-0 border font-medium ${inv.role === 'role_admin' ? 'bg-teal-50 text-teal-700 border-teal-200 hover:bg-teal-50' : 'bg-sky-50 text-sky-700 border-sky-200 hover:bg-sky-50'}`}>
                          {t[inv.role]}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-[11.5px] text-slate-400">{inv.division} · {t.expires} {inv.expires}</span>
                        <div className="flex gap-1">
                          <button
                            data-testid={`resend-${inv.id}`}
                            className="p-1.5 rounded-md text-teal-600 hover:bg-teal-50"
                            title={t.resend}
                            onClick={() => toast.success(`${t.resent_toast} ${inv.email}`)}
                          ><Send size={13} /></button>
                          <button
                            data-testid={`revoke-${inv.id}`}
                            className="p-1.5 rounded-md text-rose-500 hover:bg-rose-50"
                            title={t.revoke}
                            onClick={() => { setInvites((v) => v.filter((x) => x.id !== inv.id)); toast.error(`${t.revoked_toast} ${inv.name}`); }}
                          ><X size={13} /></button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Swish accounts */}
            <div data-testid="panel-swish-accounts" className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-[15px] font-semibold tracking-tight" style={{ fontFamily: "'Work Sans', sans-serif" }}>{t.swish_accounts}</h2>
                <Button variant="ghost" size="sm" className="h-7 text-[12px] text-teal-700 hover:bg-teal-50 hover:text-teal-800 px-2" onClick={() => toast(t.proto_toast, { icon: '🧪' })}>
                  + {t.add_account}
                </Button>
              </div>
              <div className="space-y-2.5">
                {SWISH_ACCOUNTS.map((a) => (
                  <div key={a.id} className="border border-slate-100 rounded-lg px-3 py-2.5 flex items-center gap-3">
                    <span className="w-8 h-8 rounded-lg bg-teal-50 text-teal-700 flex items-center justify-center shrink-0"><Wallet size={15} /></span>
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-semibold truncate flex items-center gap-1.5">
                        {a.label}
                        {a.primary && <Badge className="bg-teal-600 text-white shadow-none hover:bg-teal-600 text-[10px] px-1.5 py-0">{t.primary}</Badge>}
                      </div>
                      <div className="text-[11.5px] text-slate-400 tabular-nums">{a.number} · {a.linked} {t.linked}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <p className="text-center text-[11.5px] text-slate-300 pb-4">PayMig Organisation Workspace — Owner ERP Dashboard · Slice C prototype · {new Date().getFullYear()}</p>
        </main>
      </div>

      {/* ===== Division drill-down drawer ===== */}
      <Sheet open={!!sel} onOpenChange={(o) => { if (!o) setSel(null); }}>
        <SheetContent className="w-[440px] sm:max-w-[440px] overflow-y-auto" data-testid="division-drawer">
          {sel && (
            <>
              <SheetHeader className="space-y-1">
                <div className="flex items-center gap-2">
                  <SheetTitle style={{ fontFamily: "'Work Sans', sans-serif" }}>{sel.name}</SheetTitle>
                  <StatusBadge status={sel.status} t={t} />
                </div>
                <SheetDescription>{t.drawer_desc}</SheetDescription>
              </SheetHeader>

              <div className="mt-5 space-y-5" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                {/* KPI trio */}
                <div className="grid grid-cols-3 gap-2.5">
                  {[
                    [t.d_collected, fmtKr(sel.collected * scale)],
                    [t.d_pool, fmtKr(sel.pool)],
                    [t.d_tx, new Intl.NumberFormat('sv-SE').format(sel.tx)],
                  ].map(([label, value]) => (
                    <div key={label} className="border border-slate-200 rounded-lg p-2.5">
                      <div className="text-[10.5px] text-slate-400 font-medium leading-tight">{label}</div>
                      <div className="text-[15px] font-semibold tabular-nums mt-1">{value}</div>
                    </div>
                  ))}
                </div>

                {/* mini trend */}
                <div className="border border-slate-200 rounded-lg p-3">
                  <div className="text-[12px] font-semibold text-slate-500 mb-1">{t.collections_over_time}</div>
                  <Spark data={sel.trend} color="#0f766e" />
                </div>

                {/* people + payout */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-[12.5px] text-slate-500">{t.d_admin}</span>
                    <span className="text-[13px] font-semibold">{sel.admin}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[12.5px] text-slate-500">{t.d_mods}</span>
                    <span className="text-[13px]">{sel.mods.length ? sel.mods.join(', ') : '—'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[12.5px] text-slate-500">{t.d_payout}</span>
                    <PayoutCell
                      number={sel.payout}
                      revealed={!!revealed[sel.id]}
                      onToggle={() => setRevealed((r) => ({ ...r, [sel.id]: !r[sel.id] }))}
                      t={t}
                    />
                  </div>
                </div>

                {/* recent tx */}
                <div>
                  <div className="text-[12px] font-semibold text-slate-500 mb-2">{t.d_recent_tx}</div>
                  <div className="space-y-1.5">
                    {DIV_TX.map((tx, i) => (
                      <div key={i} className="flex items-center justify-between border border-slate-100 rounded-lg px-3 py-2">
                        <div>
                          <div className="text-[12.5px] font-medium">{lang === 'sv' ? tx.sv : tx.en}</div>
                          <div className="text-[11px] text-slate-400">{tx.time}</div>
                        </div>
                        <span className="text-[13px] font-semibold tabular-nums text-teal-700">+{fmtKr(tx.amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex gap-2 pt-1">
                  <Button className="flex-1 bg-teal-700 hover:bg-teal-800 text-white h-9 text-[13px]" onClick={() => toast(t.proto_toast, { icon: '🧪' })}>
                    <BarChart3 size={14} className="mr-1.5" />{t.view_report}
                  </Button>
                  <Button variant="outline" className="flex-1 h-9 text-[13px] border-slate-200" onClick={() => toast(t.proto_toast, { icon: '🧪' })}>
                    <Users size={14} className="mr-1.5" />{t.manage_members}
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
